/**
 * 信令消息处理模块，负责消费房间广播事件。
 */
import {
	getCurrentPeer,
	updateDeviceResolution,
	updateIceServers,
} from "@/services/remote/webrtc";
import { useConnectionStore, useDeviceStore } from "@/stores/remote";
import type {
	IceServersPayload,
	RoomMessageEnvelope,
	ScreenInfo,
	SignalMessage,
	SignalMessageType,
} from "@/types/remote";
import { leaveRoom, sendIceRestartAnswer } from "./api";

// 消息类型常量
const MESSAGE_TYPES = {
	PONG: "pong",
	ANSWER: "answer",
	CANDIDATE: "candidate",
	BYE: "bye",
	SCREEN_INFO: "screen-info",
	ICE_RESTART_OFFER: "ice-restart-offer",
	ICE_RESTART_ANSWER: "ice-restart-answer",
	ICE_SERVERS: "ice-servers",
} as const satisfies Record<string, SignalMessageType>;

// 验证消息格式：仅检查结构，未知 type 交由 switch 的 default 分支处理
const validateMessage = (message: unknown): message is SignalMessage => {
	if (!message || typeof message !== "object") {
		throw new Error("Invalid message format: message must be an object");
	}

	const type = (message as { type?: unknown }).type;
	if (!type || typeof type !== "string") {
		throw new Error("Invalid message format: missing or invalid type field");
	}

	return true;
};

// 处理心跳消息
const handlePongMessage = () => {
	const timestamp = Date.now();
	useConnectionStore.getState().setLastHeartbeat(timestamp);
	console.log(
		`[Room] Heartbeat updated at ${new Date(timestamp).toISOString()}`,
	);
};

// 处理 WebRTC answer 消息
const handleAnswerMessage = (content: string) => {
	try {
		const pc = getCurrentPeer();
		if (!pc) {
			throw new Error("WebRTC peer connection not available");
		}

		console.log("[Room] Received WebRTC answer:", content);
		pc.handleRemoteAnswer(content);
	} catch (error) {
		console.error("[Room] Failed to handle answer message:", error);
		throw error;
	}
};

// 处理 ICE candidate 消息
const handleCandidateMessage = (content: RTCIceCandidateInit) => {
	try {
		const pc = getCurrentPeer();
		if (!pc?.peer) {
			throw new Error("WebRTC peer connection not available");
		}

		console.log("[Room] Received ICE candidate:", content);
		pc.peer.addIceCandidate(content);
	} catch (error) {
		console.error("[Room] Failed to handle candidate message:", error);
		throw error;
	}
};

// 处理断开连接消息
const handleByeMessage = (
	room: string,
	message: SignalMessage,
	from?: string,
) => {
	console.log(
		`[Room] Received bye message from ${from} in room ${room}:`,
		message,
	);

	try {
		leaveRoom();
		const pc = getCurrentPeer();
		if (pc) {
			pc.destroy();
		}
	} catch (error) {
		console.error("[Room] Failed to handle bye message:", error);
	}
};

// 处理屏幕信息消息
const handleScreenInfoMessage = (content: ScreenInfo) => {
	try {
		if (!content || typeof content !== "object") {
			throw new Error("Invalid screen info content");
		}

		console.log("[Room] Received screen info:", content);
		useDeviceStore.getState().updateScreenResolution(content);
		updateDeviceResolution();
	} catch (error) {
		console.error("[Room] Failed to handle screen info message:", error);
		throw error;
	}
};

// 处理 ICE restart offer
const handleIceRestartOfferMessage = async (content: string) => {
	try {
		console.log("[Room] Received ICE restart offer:", content);
		const pc = getCurrentPeer();
		if (!pc) {
			throw new Error("WebRTC peer connection not available");
		}

		// 处理 ICE restart offer 并获取 answer
		const answer = await pc.handleIceRestartOffer(content);

		// 发送 answer 回对方
		await sendIceRestartAnswer(answer);
		console.log("[Room] ICE restart answer sent back successfully");
	} catch (error) {
		console.error("[Room] Failed to handle ICE restart offer:", error);
		throw error;
	}
};

// 处理 ICE restart answer
const handleIceRestartAnswerMessage = async (content: string) => {
	try {
		console.log("[Room] Received ICE restart answer:", content);
		const pc = getCurrentPeer();
		if (!pc) {
			throw new Error("WebRTC peer connection not available");
		}

		// 处理 ICE restart answer
		await pc.handleIceRestartAnswer(content);
	} catch (error) {
		console.error("[Room] Failed to handle ICE restart answer:", error);
		throw error;
	}
};

// 解析 ICE 服务列表
const parseIceServers = (message: SignalMessage): RTCIceServer[] | null => {
	if (!message) {
		return null;
	}

	// message.content 既可能是数组也可能是对象
	if (Array.isArray(message.content as IceServersPayload)) {
		return message.content as RTCIceServer[];
	}

	if (
		message.content &&
		typeof message.content === "object" &&
		Array.isArray(
			(message.content as { iceServers?: RTCIceServer[] }).iceServers,
		)
	) {
		return (message.content as { iceServers: RTCIceServer[] }).iceServers;
	}

	return null;
};

// 处理 ICE 服务器下发消息
const handleIceServersMessage = (
	message: Extract<SignalMessage, { type: "ice-servers" }>,
) => {
	try {
		const iceServers = parseIceServers(message);
		if (!iceServers) {
			console.warn("[Room] ICE servers payload missing:", message);
			return;
		}

		updateIceServers(iceServers);
		if (typeof message?.ttl === "number") {
			console.log(`[Room] ICE servers updated, ttl (seconds): ${message.ttl}`);
		} else {
			console.log("[Room] ICE servers updated");
		}
	} catch (error) {
		console.error("[Room] Failed to handle ice-servers message:", error);
		throw error;
	}
};

export const handleRoomMessage = async ({
	room,
	message,
	from,
}: RoomMessageEnvelope<unknown>) => {
	console.log(`[Room] Received message from ${from} in room ${room}:`, message);

	try {
		// 验证消息格式
		if (!validateMessage(message)) {
			throw new Error("Invalid message format");
		}

		const { type, content } = message;

		// 使用 switch 语句提高可读性和性能
		switch (type) {
			case MESSAGE_TYPES.PONG:
				handlePongMessage();
				break;

			case MESSAGE_TYPES.ANSWER:
				handleAnswerMessage(content);
				break;

			case MESSAGE_TYPES.CANDIDATE:
				handleCandidateMessage(content);
				break;

			case MESSAGE_TYPES.BYE:
				handleByeMessage(room, message, from);
				break;

			case MESSAGE_TYPES.SCREEN_INFO:
				handleScreenInfoMessage(content);
				break;

			case MESSAGE_TYPES.ICE_RESTART_OFFER:
				await handleIceRestartOfferMessage(content);
				break;

			case MESSAGE_TYPES.ICE_RESTART_ANSWER:
				await handleIceRestartAnswerMessage(content);
				break;

			case MESSAGE_TYPES.ICE_SERVERS:
				handleIceServersMessage(
					message as Extract<SignalMessage, { type: "ice-servers" }>,
				);
				break;

			default:
				console.warn(`[Room] Unknown message type: ${type}`, message);
				break;
		}
	} catch (error) {
		console.error(
			`[Room] Error handling message from ${from} in room ${room}:`,
			error,
		);
		console.error("[Room] Message details:", { room, message, from });

		// 对于关键错误，可以考虑断开连接
		if (error.message.includes("WebRTC peer connection not available")) {
			console.error("[Room] Critical WebRTC error, consider reconnecting");
		}
	}
};
